import React, { useEffect, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import { InvestorProfile as InvestorProfileType } from '../types/firestore';
import InvestorProfileComponent from '../components/profile/InvestorProfile';

const InvestorProfileViewPage: React.FC = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<InvestorProfileType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const functions = getFunctions();

  useEffect(() => {
    const loadProfile = async () => {
      if (!user?.id) {
        setError('User not authenticated');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const getInvestorProfile = httpsCallable(functions, 'getInvestorProfile');
        const result = await getInvestorProfile({ userId: user.id });
        const data = result.data as any;

        if (data?.success && data.profile) {
          // Convert Firestore timestamps
          const profileWithDates = {
            ...data.profile,
            createdAt: data.profile.createdAt?.seconds
              ? new Date(data.profile.createdAt.seconds * 1000)
              : new Date(),
            updatedAt: data.profile.updatedAt?.seconds
              ? new Date(data.profile.updatedAt.seconds * 1000)
              : new Date(),
          };
          setProfile(profileWithDates);
        } else {
          setError('Profile not found');
        }
      } catch (err: any) {
        console.error('Error loading profile:', err);
        setError(err.message || 'Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [user, functions]);

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-950 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500 mb-4"></div>
          <p className="text-white text-lg">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-dark-950 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-lg">{error || 'Profile not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-950 py-2">
      <div className="container mx-auto">
        <InvestorProfileComponent profile={profile} isOwnProfile />
      </div>
    </div>
  );
};

export default InvestorProfileViewPage;
